import {
  Anchor,
  Button,
  Card,
  Group,
  LoadingOverlay,
  Pagination,
  Skeleton,
  Table,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { IconChevronDown, IconTableExport } from '@tabler/icons-react';
import { ExportDebtorCsv } from 'app/Private/Clients';
import { ClientHoverCard } from 'components/ClientHoverCard';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import { cloneDeep, mergeWith, subtract } from 'lodash-es';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { chunk, isDefined, times } from 'remeda';
import type { AgedBalance, Balance } from 'shared/hooks/use-load-aged-balance';
import { useAgedBalanceColors } from 'shared/hooks/utils/use-aged-balance-colors';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';

function computeNetAgedBalancePerDebtor(agedBalance: AgedBalance['data']) {
  return agedBalance.map(({ debits, credits, debtorId, debtorName }) => {
    const merged = mergeWith(cloneDeep(debits), credits, subtract);
    return {
      debtorId,
      debtorName,
      ...merged,
      totalBalance: Object.values(merged).reduce((acc, curr) => acc + curr, 0),
    };
  });
}

function formatAmountWithColor(value: number, color: string, localizedValue: string) {
  if (value === 0) return localizedValue;

  return (
    <Text fw={600} c={color}>
      {localizedValue}
    </Text>
  );
}

function isKeyOfBalance(input: string): input is keyof Balance {
  return ['0', '30', '60', '90', 'totalBalance', 'notDue'].includes(input);
}

interface HeadCellProps {
  columnKey: string;
  onClick?: (columnName: keyof Balance) => void;
  isSelected?: boolean;
}

const HeadCell = ({ columnKey, onClick, isSelected = false }: HeadCellProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const isClickable = isKeyOfBalance(columnKey) && onClick != null;

  return (
    <Group
      pb={6}
      gap={4}
      onClick={isClickable ? () => onClick(columnKey) : () => {}}
      style={{ cursor: isClickable ? 'pointer' : undefined, userSelect: 'none' }}
    >
      <Text
        fz={16}
        fw={isSelected ? 900 : 600}
        c={isSelected ? theme.colors.purple[5] : theme.colors.gray[7]}
      >
        {t(tKeysLoose.ANALYTICS.AGED_BALANCE[columnKey])}
      </Text>
      {isClickable ? (
        <IconChevronDown
          size={15}
          stroke={isSelected ? 2 : 1.5}
          color={isSelected ? theme.colors.purple[4] : theme.colors.gray[7]}
        />
      ) : null}
    </Group>
  );
};

const skeletonRow = () => [
  <Skeleton height={20} w="80%" />,
  <Skeleton height={20} w="80%" />,
  <Skeleton height={20} w="80%" />,
  <Skeleton height={20} w="80%" />,
  <Skeleton height={20} w="80%" />,
  <Skeleton height={20} w="80%" />,
];

interface AgedBalanceTableProps {
  agedBalance?: AgedBalance;
  isLoading: boolean;
}

export const AgedBalanceTable = ({ agedBalance, isLoading }: AgedBalanceTableProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const colors = useAgedBalanceColors();
  const formatter = useLocalizedCurrencyFormatter();
  const {
    preferences: { itemsPerPage },
  } = useProfile();

  const [pageIndex, setPageIndex] = useState(1);
  const [sortingColumn, setSortingColumn] = useState<keyof Balance | 'totalBalance'>(
    'totalBalance',
  );

  const loadingRows = times(7, () => skeletonRow());

  const chunkedRows = useMemo(() => {
    if (isLoading && !isDefined(agedBalance)) return [];

    const netAgedBalancePerDebtor = isDefined(agedBalance)
      ? computeNetAgedBalancePerDebtor(agedBalance.data)
      : [];

    const sorted = netAgedBalancePerDebtor
      .slice()
      .sort((a, b) => b[sortingColumn] - a[sortingColumn]);

    // Cant't destructure bare numbers used as keys
    const netAgedBalancePerDebtorRows = sorted.map(
      ({
        debtorName,
        debtorId,
        0: zero,
        30: thirty,
        60: sixty,
        90: ninety,
        totalBalance,
        notDue,
      }) => [
        <ClientHoverCard debtorId={String(debtorId)} debtorName={debtorName}>
          <Anchor
            underline="always"
            c={theme.colors.gray[9]}
            fw={400}
            component={Link}
            to={`/clients/${debtorId}#balance-detail`}
          >
            {debtorName}
          </Anchor>
        </ClientHoverCard>,
        formatAmountWithColor(notDue, theme.colors.gray[9], formatter.format(notDue)),
        formatAmountWithColor(zero, colors[0], formatter.format(zero)),
        formatAmountWithColor(thirty, colors[30], formatter.format(thirty)),
        formatAmountWithColor(sixty, colors[60], formatter.format(sixty)),
        formatAmountWithColor(ninety, colors[90], formatter.format(ninety)),
        formatAmountWithColor(totalBalance, theme.colors.gray[9], formatter.format(totalBalance)),
      ],
    );

    return chunk(netAgedBalancePerDebtorRows, itemsPerPage);
  }, [agedBalance, itemsPerPage, sortingColumn, theme.colors.gray, isLoading, colors]);

  const isPaginated = chunkedRows.length > 1;

  const formatedComputedAt = isDefined(agedBalance)
    ? dayjs(agedBalance.computedAt).format('LL')
    : '';

  const onClickColumnHead = (columnName: keyof Balance) => {
    setSortingColumn(columnName);
    setPageIndex(1);
  };

  const handleExport = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(i18nKeys.EXPORT),
      children: (
        <ExportDebtorCsv
          overrideDefaultSelection={[
            'aged_balance_not_due',
            'aged_balance_within_30',
            'aged_balance_between_30_and_60',
            'aged_balance_between_60_and_90',
            'aged_balance_above_90',
            'debtor_remaining_balance',
            'full_name',
          ]}
        />
      ),
    });
  };

  return (
    <Panel
      flushContent
      title={t(i18nKeys.ANALYTICS.AGED_BALANCE.TABLE_TITLE, {
        date: formatedComputedAt,
      })}
      withBorder
      topRight={
        <Button
          onClick={handleExport}
          variant="default"
          size="compact-sm"
          leftSection={<IconTableExport stroke="1.5" size={14} />}
        >
          {t(i18nKeys.EXPORT)}
        </Button>
      }
      footer={
        isPaginated ? (
          <Card.Section withBorder inheritPadding pb="md">
            <Group w="100%" justify="flex-end" mt="md">
              <Pagination
                size="sm"
                total={chunkedRows.length}
                value={pageIndex}
                onChange={setPageIndex}
              />
            </Group>
          </Card.Section>
        ) : null
      }
    >
      <Table
        c={theme.colors.gray[6]}
        striped
        data={{
          head: [
            <HeadCell columnKey="DEBTOR" />,
            ...['notDue', '0', '30', '60', '90', 'totalBalance'].map(key => (
              <HeadCell
                columnKey={key}
                isSelected={key === sortingColumn}
                onClick={onClickColumnHead}
              />
            )),
          ],
          body: isLoading && !isDefined(agedBalance) ? loadingRows : chunkedRows[pageIndex - 1],
        }}
      />
      <LoadingOverlay visible={isLoading && isDefined(agedBalance)} />
    </Panel>
  );
};
